import { useState, useEffect } from "react";
import "./App.css";
import "./components/AIAnalysis.css";
import Header from "./components/Header";
import TabBar from "./components/TabBar";
import SentimentBar from "./components/SentimentBar";
import EmailInfo from "./components/EmailInfo";
import SalesforceContext from "./components/SalesforceContext";
import ActivePolicies from "./components/ActivePolicies";
import CrossSell from "./components/CrossSell";
import Footer from "./components/Footer";
import mockData from "./data/mockData";
import AIAnalysis from "./components/AiAnalysis";
import Actions from "./components/Actions";
import TemplatesTab from "./components/TemplatesTab";
import AnalyticsModal from "./components/AnalyticsModal";
import apiService from "./services/apiService";
import API_BASE_URL from "./config";
import { useRef, useCallback } from "react";
import Settings from "./components/Settings";

/* global Office */

const SENTIMENT_CONFIG = {
  Urgent: { emoji: "🔴", color: "#ef4444" },
  Negative: { emoji: "😟", color: "#ef4444" },
  Neutral: { emoji: "😐", color: "#f59e0b" },
  Positive: { emoji: "😊", color: "#059669" },
};

const INITIAL_SEARCH_RESULTS_COUNT = 5;
const createEmptyCrossSell = () => ({
  count: 0,
  opportunities: [],
});

const getOutlookMessageMetadata = () => {
  try {
    const mailbox = Office.context?.mailbox;
    const item = mailbox?.item;

    return {
      messageId: item?.internetMessageId || item?.itemId || "",
      userEmail: mailbox?.userProfile?.emailAddress || "",
    };
  } catch (error) {
    console.log("Failed to read Outlook message metadata", error);
    return { messageId: "", userEmail: "" };
  }
};

const getOutlookUserProfile = () => {
  try {
    const mailbox = Office.context?.mailbox;

    return {
      userEmail: mailbox?.userProfile?.emailAddress || "",
      userName: mailbox?.userProfile?.displayName || "",
    };
  } catch (error) {
    console.log("Failed to read Outlook user profile", error);
    return { userEmail: "", userName: "" };
  }
};

const App = () => {
  const [activeTab, setActiveTab] = useState("Context");
  const [emailInfo, setEmailInfo] = useState(mockData.emailInfo);
  const [sentiment, setSentiment] = useState(mockData.sentiment);
  const [contextLoading, setContextLoading] = useState(false);
  const [sfLoading, setSfLoading] = useState(false);
  const [sfContext, setSfContext] = useState({});
  const [policies, setPolicies] = useState([]);
  const [sfAuthError, setSfAuthError] = useState(null);
  const [sfAuthRefreshing, setSfAuthRefreshing] = useState(false);
  const [sfSession, setSfSession] = useState({ accessToken: null, instanceUrl: null });
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [showAllResults, setShowAllResults] = useState(false);
  const [showSearchUI, setShowSearchUI] = useState(false);
  const [manualSearchAttempted, setManualSearchAttempted] = useState(false);
  const [pendingSfMatch, setPendingSfMatch] = useState(null);
  const [crossSell, setCrossSell] = useState(createEmptyCrossSell);
  const [crossSellLoading, setCrossSellLoading] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const sfSessionRef = useRef({ accessToken: null, instanceUrl: null });
  const crossSellRequestRef = useRef(0);
  const lastSfMatchKeyRef = useRef("");
  const analyticsUserEmail = getOutlookMessageMetadata().userEmail;

  const resetManualSearchState = useCallback(() => {
    setSearchTerm("");
    setSearchResults([]);
    setShowAllResults(false);
    setManualSearchAttempted(false);
    setSfContext((prev) => ({ ...prev, error: null }));
  }, []);

  const resetCrossSellState = useCallback(() => {
    crossSellRequestRef.current += 1;
    setCrossSell(createEmptyCrossSell());
    setCrossSellLoading(false);
  }, []);

  const handleOpenSearchUI = useCallback(() => {
    resetManualSearchState();
    setShowSearchUI(true);
  }, [resetManualSearchState]);

  const handleCloseSearchUI = useCallback(() => {
    resetManualSearchState();
    setShowSearchUI(false);
  }, [resetManualSearchState]);

  const fetchSfToken = useCallback(async () => {
    setSfAuthRefreshing(true);
    try {
      /* 
      // Original Salesforce token fetching logic (Commented out as requested)
      const data = await apiService.getSalesforceToken();
      if (data.error) {
        setSfAuthError(data);
        const emptySession = { accessToken: null, instanceUrl: null };
        setSfSession(emptySession);
        sfSessionRef.current = emptySession;
        return null;
      }
      const session = { accessToken: data.access_token, instanceUrl: data.instance_url };
      */

      // Using MOCK tokens for Salesforce session to avoid real API requests
      const session = { 
        accessToken: "mock_access_token_123", 
        instanceUrl: "https://mock-instance.salesforce.com" 
      };
      
      setSfSession(session);
      sfSessionRef.current = session;
      setSfAuthError(null);
      console.log("Mock Salesforce token stored in memory");
      return session;
    } catch (error) {
      console.error("Failed to initialize Salesforce session:", error);
      setSfAuthError({ error: "Failed to connect to Salesforce service" });
      const emptySession = { accessToken: null, instanceUrl: null };
      setSfSession(emptySession);
      sfSessionRef.current = emptySession;
      return null;
    } finally {
      setSfAuthRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSfToken();

    // Office.js Initialization
    try {
      const outlookUserProfile = getOutlookUserProfile();
      
      // Restored real backend call for Outlook user registration
      if (outlookUserProfile.userEmail) {
        apiService.registerOutlookUser(outlookUserProfile).catch((error) => {
          console.error("Outlook user registration failed:", error);
        });
      }

      /*
      // Mock registration bypass (Commented out)
      if (outlookUserProfile.userEmail) {
        console.log("Mock registration for:", outlookUserProfile.userEmail);
      }
      */

      const item = Office.context?.mailbox?.item;
      const userProfile = Office.context?.mailbox?.userProfile;

      // Restored real backend call for user profile fetching
      if (userProfile && userProfile.emailAddress) {
        apiService.getOrCreateUser(userProfile.emailAddress, userProfile.displayName)
          .then(user => {
            console.log("Logged in user:", user);
            setCurrentUser(user);
          })
          .catch(err => {
            console.error("Failed to fetch/create user profile:", err);
          });
      }

      /*
      // Using MOCK data for current user profile (Commented out)
      if (userProfile && userProfile.emailAddress) {
        setCurrentUser({
          email: userProfile.emailAddress,
          name: userProfile.displayName,
          role: "admin" // Mocking as admin for full access
        });
      }
      */

      if (item) {
        // Set sender info from Office.js
        setEmailInfo((prev) => ({
          ...prev,
          from: item.from?.displayName || prev.from,
          email: item.from?.emailAddress || prev.email,
        }));

        // Get email body and call AI
        item.body.getAsync("text", async (result) => {
          if (result.status === "succeeded" && result.value) {
            await analyzeContext(result.value, getOutlookMessageMetadata());
          }
        });
      }
    } catch (e) {
      console.log("Office.js not available, using mock data");
    }
  }, []);

  const analyzeContext = async (emailBody, metadata = getOutlookMessageMetadata()) => {
    setContextLoading(true);
    try {
      // Restored real backend call for email context analysis
      const data = await apiService.analyzeEmailContext({
        emailBody,
        messageId: metadata.messageId,
        userEmail: metadata.userEmail,
      });

      /*
      // Mock data bypass (Commented out)
      const mockResult = {
        sentiment: "Positive",
        sentimentSummary: "The client seems satisfied with the current service.",
        phone: "(555) 012-3456",
        policyNumber: "PA-2024-78432",
        nameInsured: "John Smith",
        email: "john.smith@example.com"
      };
      const data = mockResult;
      */

      // Update sentiment
      if (data.sentiment) {
        const config = SENTIMENT_CONFIG[data.sentiment] || SENTIMENT_CONFIG.Neutral;
        setSentiment({
          level: data.sentiment,
          emoji: config.emoji,
          color: config.color,
          message: data.sentimentSummary || "",
        });
      }

      // Update phone and policy number from AI
      setEmailInfo((prev) => ({
        ...prev,
        phone: data.phone || prev.phone,
        policyMentioned: data.policyNumber || prev.policyMentioned,
        nameInsured: data.nameInsured || prev.nameInsured,
      }));

      const nextPendingSfMatch = {
        messageId: metadata.messageId || "",
        policyNumber: data.policyNumber || "",
        nameInsured: data.nameInsured || "",
        email: data.email || "",
        phone: data.phone || "",
      };

      if (
        nextPendingSfMatch.policyNumber ||
        nextPendingSfMatch.nameInsured ||
        nextPendingSfMatch.email ||
        nextPendingSfMatch.phone
      ) {
        setPendingSfMatch(nextPendingSfMatch);
      } else {
        setPendingSfMatch(null);
        setSfContext({ matchFound: false });
        setPolicies([]);
        resetCrossSellState();
      }
    } catch (error) {
      console.error("Context analysis failed:", error);
    } finally {
      setContextLoading(false);
    }
  };

  const fetchCrossSellOpportunities = useCallback(async (matchResult) => {
    const policiesForCrossSell = Array.isArray(matchResult?.policies) ? matchResult.policies : [];

    if (!policiesForCrossSell.length && !matchResult.mock) {
      resetCrossSellState();
      return;
    }

    const requestId = ++crossSellRequestRef.current;
    setCrossSellLoading(true);

    try {
      // Restored real backend call for cross-sell opportunities
      const metadata = getOutlookMessageMetadata();
      const response = await apiService.generateCrossSellOpportunities({
        messageId: metadata.messageId,
        userEmail: metadata.userEmail,
        accountName: matchResult.accountName || "",
        contactName: matchResult.contacts?.[0]?.name || "",
        clientSince: matchResult.clientSince || "",
        openCases: matchResult.openCasesCount ?? null,
        policies: policiesForCrossSell,
      });

      /*
      // Mock data bypass (Commented out)
      const response = mockData.crossSell;
      */

      if (requestId !== crossSellRequestRef.current) return;

      const opportunities = Array.isArray(response?.opportunities) ? response.opportunities : [];
      setCrossSell({
        count: typeof response?.count === "number" ? response.count : opportunities.length,
        opportunities,
      });
    } catch (error) {
      console.error("Cross-sell generation failed:", error);
      if (requestId !== crossSellRequestRef.current) return;
      setCrossSell(createEmptyCrossSell());
    } finally {
      if (requestId === crossSellRequestRef.current) {
        setCrossSellLoading(false);
      }
    }
  }, [resetCrossSellState]);

  const handleSfResult = useCallback((res) => {
    if (res.status === "FOUND" || res.status === "SUCCESS") {
      setSfContext({
        matchFound: true,
        accountName: res.accountName,
        contact: res.contacts?.[0]?.name || "N/A",
        contacts: res.contacts || [],
        clientSince: res.clientSince,
        openCases: res.openCasesCount,
        accountId: res.accountId
      });

      const mappedPolicies = (res.policies || []).map((p, idx) => {
        const lobMap = {
          AUTOB: { icon: "🚗", type: "Auto Policy" },
          CPKGE: { icon: "📦", type: "Commercial Package" },
          CUMBR: { icon: "☂️", type: "Umbrella Policy" },
          PROP: { icon: "🏠", type: "Property Policy" },
          CGL: { icon: "🛡️", type: "General Liability" }
        };
        const info = lobMap[p.lineOfBusiness] || { icon: "📄", type: p.lineOfBusiness || "Unknown" };
        return {
          id: p.policyId || idx,
          type: info.type,
          icon: info.icon,
          status: p.status,
          policyNumber: p.policyNumber,
          carrier: p.carrier,
          premium: `$${p.premiumAmount?.toLocaleString()}`,
          expiration: p.expirationDate?.split(" ")[0],
          details: p.lineOfBusiness
        };
      });
      setPolicies(mappedPolicies);
      resetManualSearchState();
      setShowSearchUI(false); // Hide search UI on successful match
      fetchCrossSellOpportunities(res);
    } else {
      setSfContext({ matchFound: false });
      setPolicies([]);
      resetCrossSellState();
    }
  }, [fetchCrossSellOpportunities, resetCrossSellState, resetManualSearchState]);

  useEffect(() => {
    if (!pendingSfMatch || sfAuthError) return;

    const { accessToken, instanceUrl } = sfSession;
    if (!accessToken || !instanceUrl) return;

    const hasLookupValue = Boolean(
      pendingSfMatch.policyNumber ||
      pendingSfMatch.nameInsured ||
      pendingSfMatch.email ||
      pendingSfMatch.phone
    );
    if (!hasLookupValue) return;

    const requestKey = JSON.stringify({
      messageId: pendingSfMatch.messageId || "",
      policyNumber: pendingSfMatch.policyNumber || "",
      nameInsured: pendingSfMatch.nameInsured || "",
      email: pendingSfMatch.email || "",
      phone: pendingSfMatch.phone || "",
    });

    if (lastSfMatchKeyRef.current === requestKey) return;
    lastSfMatchKeyRef.current = requestKey;

    console.log("Using Mock Salesforce match data for extracted context");
    setSfLoading(true);

    // Bypassing real Salesforce match API and using mock data
    setTimeout(() => {
      const mockSfResult = {
        status: "FOUND",
        accountName: mockData.salesforceContext.account,
        accountId: "mock_account_id_001",
        clientSince: mockData.salesforceContext.clientSince,
        openCasesCount: mockData.salesforceContext.openCases,
        contacts: [{ name: mockData.salesforceContext.contact }],
        policies: mockData.activePolicies.map(p => ({
          policyId: p.id,
          policyNumber: p.policyNumber,
          lineOfBusiness: p.type === "Auto Policy" ? "AUTOB" : (p.type === "Home Policy" ? "PROP" : "CGL"),
          status: p.status,
          carrier: p.carrier,
          premiumAmount: parseInt(p.premium.replace(/[^0-9]/g, '')),
          expirationDate: p.expiration
        })),
        mock: true
      };
      handleSfResult(mockSfResult);
      setSfLoading(false);
    }, 800);

    /*
    // Original Salesforce matching logic (Commented out as requested)
    apiService.matchAccount({
      instance_url: instanceUrl,
      access_token: accessToken,
      policyNumber: pendingSfMatch.policyNumber || "",
      nameInsured: pendingSfMatch.nameInsured || "",
      email: pendingSfMatch.email || "",
      phone: pendingSfMatch.phone || "",
    })
      .then((res) => {
        handleSfResult(res);
      })
      .catch((err) => {
        console.error("SFDC Match Error:", err);
        setSfContext({ matchFound: false });
        resetCrossSellState();
      })
      .finally(() => {
        setSfLoading(false);
      });
    */
  }, [pendingSfMatch, sfAuthError, sfSession, handleSfResult, resetCrossSellState]);

  const handleSelectAccount = (account) => {
    const { accessToken: token, instanceUrl } = sfSessionRef.current;
    if (!token || !instanceUrl) return;

    setSfLoading(true);
    setSearchResults([]); // Clear list while loading details

    // Bypassing real Salesforce account selection and using mock details
    setTimeout(() => {
      const mockSfResult = {
        status: "FOUND",
        accountName: account.accountName || account.name,
        accountId: account.accountId || "mock_selected_id",
        clientSince: "January 2020",
        openCasesCount: 0,
        contacts: [{ name: "Mock Selected Contact" }],
        policies: mockData.activePolicies.map(p => ({
          policyId: p.id,
          policyNumber: p.policyNumber,
          lineOfBusiness: p.type === "Auto Policy" ? "AUTOB" : (p.type === "Home Policy" ? "PROP" : "CGL"),
          status: p.status,
          carrier: p.carrier,
          premiumAmount: 1200,
          expirationDate: p.expiration
        })),
        mock: true
      };
      handleSfResult(mockSfResult);
      setSfLoading(false);
    }, 500);

    /*
    // Original account selection logic (Commented out as requested)
    apiService.matchAccount({
      instance_url: instanceUrl,
      access_token: token,
      nameInsured: account.accountName || account.name || "",
      policyNumber: account.policyNumber || "",
      email: account.email || "",
      phone: account.phone || "",
    })
      .then((res) => {
        handleSfResult(res);
      })
      .catch((err) => {
        console.error("SFDC Match Error after selection:", err);
        setSfContext({ matchFound: false, error: "Failed to fetch account details." });
      })
      .finally(() => {
        setSfLoading(false);
      });
    */
  };

  const handleManualSearch = async () => {
    if (!searchTerm.trim()) return;
    const trimmedSearchTerm = searchTerm.trim();

    const { accessToken: token, instanceUrl } = sfSessionRef.current;
    if (!token || !instanceUrl) {
      const session = await fetchSfToken();
      if (!session) return;
    }

    setSfLoading(true);
    setManualSearchAttempted(true);
    try {
      setSfContext((prev) => ({ ...prev, error: null }));
      
      /*
      // Original manual search logic (Commented out as requested)
      const res = await apiService.searchAccount({
        instance_url: instanceUrl || sfSessionRef.current.instanceUrl,
        access_token: token || sfSessionRef.current.accessToken,
        searchTerm: trimmedSearchTerm
      });
      */

      // Using MOCK search results to avoid real API requests
      const mockSearchResults = [
        { accountName: "Mock Smith Account", policyNumber: "PA-11111", email: "smith@mock.com", accountId: "m1" },
        { accountName: "Mock Johnson Account", policyNumber: "HO-22222", email: "johnson@mock.com", accountId: "m2" },
        { accountName: "Mock Brown Account", policyNumber: "GL-33333", email: "brown@mock.com", accountId: "m3" }
      ];

      setSearchResults(mockSearchResults);
      setShowAllResults(false);
    } catch (err) {
      console.error("SFDC Manual Search Error:", err);
      setSfContext((prev) => ({ ...prev, error: "Search failed. Please try again." }));
    } finally {
      setSfLoading(false);
    }
  };

  const isSearchingDifferentAccount = showSearchUI && sfContext.matchFound;
  const hasSearchResults = searchResults.length > 0;
  const searchStatusText = hasSearchResults
    ? `Found ${searchResults.length} Salesforce ${searchResults.length === 1 ? "account" : "accounts"}. Select one below.`
    : (isSearchingDifferentAccount && !manualSearchAttempted)
      ? "Search for a different account:"
      : "No matching Salesforce account found.";
  const visibleSearchResults = showAllResults
    ? searchResults
    : searchResults.slice(0, INITIAL_SEARCH_RESULTS_COUNT);
  const remainingResultsCount = Math.max(searchResults.length - INITIAL_SEARCH_RESULTS_COUNT, 0);
  const canManageSfSettings = currentUser?.role === "admin";
  const sfAuthGuidance = canManageSfSettings
    ? "Check Salesforce keys in Settings."
    : "Ask an admin to check Salesforce settings.";

  return (
    <div className="app">
      <Header
        onAnalyticsClick={() => {
          setShowSettings(false);
          setShowAnalytics(true);
        }}
        onSettingsClick={() => {
          setShowAnalytics(false);
          setShowSettings((prev) => !prev);
        }}
      />

      {showAnalytics ? (
        <AnalyticsModal
          userEmail={analyticsUserEmail}
          onClose={() => setShowAnalytics(false)}
        />
      ) : showSettings ? (
        <Settings currentUser={currentUser} onClose={() => setShowSettings(false)} />
      ) : (
        <>
          {sfAuthError && (
            <div className="auth-error-banner">
              <span className="auth-error-icon">⚠️</span>
              <div className="auth-error-content">
                <h3>Salesforce unavailable</h3>
                <p className="auth-error-help">{sfAuthGuidance}</p>
              </div>
              <button
                type="button"
                className="auth-refresh-btn"
                onClick={fetchSfToken}
                disabled={sfAuthRefreshing}
                aria-label={sfAuthRefreshing ? "Reconnecting to Salesforce" : "Reconnect to Salesforce"}
                title={sfAuthRefreshing ? "Reconnecting..." : "Reconnect"}
              >
                <svg
                  className={sfAuthRefreshing ? "auth-refresh-icon auth-refresh-icon--spinning" : "auth-refresh-icon"}
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 2v6h-6" />
                  <path d="M3 22v-6h6" />
                  <path d="M3.5 9a9 9 0 0 1 14.13-3.36L21 8" />
                  <path d="M20.5 15a9 9 0 0 1-14.13 3.36L3 16" />
                </svg>
              </button>
            </div>
          )}

          {activeTab !== "Templates" && sfContext?.matchFound === false && (
            <div className="sf-not-found-banner">
              <h3 className="sf-not-found-heading">Salesforce account not found</h3>
            </div>
          )}

          <TabBar activeTab={activeTab} onTabChange={setActiveTab} />

          <div className="tab-content">
            {activeTab === "Context" && (
              <div className="context-tab">
                {contextLoading && (
                  <div className="context-loading">
                    <span className="spinner" /> Analyzing email...
                  </div>
                )}
                {sentiment.level && <SentimentBar sentiment={sentiment} />}
                <EmailInfo emailInfo={emailInfo} />

                {sfLoading ? (
                  <div className="section-loading">
                    <span className="spinner" /> Searching Salesforce...
                  </div>
                ) : (sfContext.matchFound && !showSearchUI) ? (
                  <>
                    <div className="section-header-row">
                      <h3 className="section-title">{sfContext.accountName || "Salesforce Account"}</h3>
                      <button className="text-action-link" onClick={handleOpenSearchUI}>Change</button>
                    </div>
                    <SalesforceContext context={sfContext} />
                    <ActivePolicies policies={policies} />
                    <CrossSell crossSell={crossSell} loading={crossSellLoading} />
                  </>
                ) : (sfContext.matchFound === false || showSearchUI) ? (
                  <div className="no-match-card">
                    {isSearchingDifferentAccount && (
                      <button className="back-link" onClick={handleCloseSearchUI}>← Back to current account</button>
                    )}
                    <span className="no-match-icon">☁️</span>
                    <p>{searchStatusText}</p>

                    <div className="manual-search-box">
                      <input
                        type="text"
                        placeholder="Search by name, policy, or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                      />
                      <button onClick={handleManualSearch} disabled={sfLoading}>
                        {sfLoading ? "Searching..." : "Search"}
                      </button>
                    </div>

                    {searchResults.length > 0 && (
                      <div className="search-results-list">
                        <p className="search-results-title">Select an account:</p>
                        <div className="search-results-scroll-container">
                          {visibleSearchResults.map((acc, idx) => (
                            <div key={acc.accountId || idx} className="search-result-item" onClick={() => handleSelectAccount(acc)}>
                              <div className="search-result-name">{acc.accountName || acc.name}</div>
                              <div className="search-result-details">
                                {acc.policyNumber && <span>Policy: {acc.policyNumber}</span>}
                                {acc.email && <span> | {acc.email}</span>}
                              </div>
                            </div>
                          ))}
                        </div>

                        {remainingResultsCount > 0 && !showAllResults && (
                          <button className="show-more-btn" onClick={() => setShowAllResults(true)}>
                            View more ({remainingResultsCount} more)
                          </button>
                        )}
                      </div>
                    )}

                    {sfContext.error && <p className="search-error">{sfContext.error}</p>}
                  </div>
                ) : null}
              </div>
            )}

            {activeTab === "AI Analysis" && (
              <div className="context-tab">
                <AIAnalysis
                  sfContext={sfContext}
                  policies={policies}
                  sfAuthError={sfAuthError}
                  sfSession={sfSession}
                  refreshSfToken={fetchSfToken}
                />
              </div>
            )}

            {activeTab === "Actions" && (
              <Actions
                onNavigateToTab={setActiveTab}
                sfContext={sfContext}
                sfAuthError={sfAuthError}
                emailInfo={emailInfo}
                sfSession={sfSession}
                refreshSfToken={fetchSfToken}
              />
            )}

            {activeTab === "Templates" && (
              <TemplatesTab
                emailInfo={emailInfo}
                salesforceContext={sfContext}
                sfAuthError={sfAuthError}
              />
            )}
          </div>

          <Footer />
        </>
      )}
    </div>
  );
};

export default App;
